import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  TextField,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  Autocomplete,
  Tabs,
  Tab,
  InputAdornment,
  MenuItem,
  IconButton,
} from "@mui/material";
import {
  Save,
  Person,
  Notifications,
  Settings,
  Security,
  Visibility,
  VisibilityOff,
  Add,
  Delete,
  AttachMoney,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";

import api from "../../api/axiosInstance";
import systemSettingsService from "../../services/systemSettingsService";
import currencyService from "../../services/currencyService";
import { fetchCurrencies } from "../../redux/slices/currenciesSlice";
import CurrencyFlag from "../../components/common/CurrencyFlag";
import { getCurrencyCountry } from "../../utils/currencyCountries";
import ALL_CURRENCIES from "../../utils/currencyCatalog";
import {
  selectCurrentUser,
  setCredentials,
} from "../../redux/slices/authSlice";
import PageHeader from "../../components/common/PageHeader";

const getNotificationDefaults = (prefs = {}) => ({
  email: {
    newShipment: prefs?.email?.newShipment ?? true,
    statusChange: prefs?.email?.statusChange ?? true,
    payment: prefs?.email?.payment ?? true,
    overdueAlert: prefs?.email?.overdueAlert ?? true,
    dailyDigest: prefs?.email?.dailyDigest ?? false,
  },
  inApp: {
    newShipment: prefs?.inApp?.newShipment ?? true,
    statusChange: prefs?.inApp?.statusChange ?? true,
    payment: prefs?.inApp?.payment ?? true,
    overdueAlert: prefs?.inApp?.overdueAlert ?? true,
    dailyDigest: prefs?.inApp?.dailyDigest ?? false,
  },
});

const blankSystem = {
  emailSendingEnabled: true,
  featureFlags: { promosEnabled: false, referralsEnabled: false },
  companyName: "",
  contactEmail: "",
  contactPhone: "",
  officeAddress: "",
  defaultDueDays: "",
  maxFileSizeMb: "",
  defaultCarrier: "",
  customsExporter: {
    name: "",
    tin: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    country: "USA",
    phone: "",
  },
  customsRegime: "",
  customsShippingAgent: "",
  customsTermsOfDelivery: "",
  customsDefaultQtyUnit: "",
  customsDefaultProcedureCode: "",
  customsDefaultCategoryOfGoods: "",
  manifestPreparedBy: "",
  manifestAuthorizedSignature: "",
  sailingCutoffs: [],
};

const WEEKDAY_OPTIONS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const AdminSettings = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [profile, setProfile] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
  });

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notificationPreferences, setNotificationPreferences] = useState(
    getNotificationDefaults(user?.notificationPreferences),
  );
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    setNotificationPreferences(
      getNotificationDefaults(user?.notificationPreferences),
    );
  }, [user]);

  const [system, setSystem] = useState(blankSystem);
  const [systemLoading, setSystemLoading] = useState(true);
  const [savingSystem, setSavingSystem] = useState(false);
  const [savingEmailToggle, setSavingEmailToggle] = useState(false);

  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currencyToAdd, setCurrencyToAdd] = useState(null);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [togglingCurrencyId, setTogglingCurrencyId] = useState(null);
  const [deletingCurrencyId, setDeletingCurrencyId] = useState(null);
  const currencyPickerOptions = ALL_CURRENCIES.filter(
    (c) => !currencies.some((existing) => existing.code === c.code),
  );

  const loadCurrencies = async () => {
    setCurrenciesLoading(true);
    try {
      const { data } = await currencyService.listAll();
      setCurrencies(data.data.currencies);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to load currencies", { variant: "error" });
    } finally {
      setCurrenciesLoading(false);
    }
  };

  useEffect(() => {
    loadCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCurrency = async () => {
    if (!currencyToAdd) {
      enqueueSnackbar("Pick a currency to add", { variant: "error" });
      return;
    }
    setSavingCurrency(true);
    try {
      await currencyService.create({ code: currencyToAdd.code, name: currencyToAdd.name });
      enqueueSnackbar(`${currencyToAdd.code} added`, { variant: "success" });
      setCurrencyToAdd(null);
      await loadCurrencies();
      dispatch(fetchCurrencies());
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to add currency", { variant: "error" });
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleToggleCurrencyActive = async (currency) => {
    setTogglingCurrencyId(currency._id);
    try {
      await currencyService.update(currency._id, { isActive: !currency.isActive });
      enqueueSnackbar(`${currency.code} ${currency.isActive ? "deactivated" : "activated"}`, { variant: "success" });
      await loadCurrencies();
      dispatch(fetchCurrencies());
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update currency", { variant: "error" });
    } finally {
      setTogglingCurrencyId(null);
    }
  };

  const handleMoveCurrency = async (index, direction) => {
    const swapWith = index + direction;
    if (swapWith < 0 || swapWith >= currencies.length) return;

    const reordered = [...currencies];
    [reordered[index], reordered[swapWith]] = [reordered[swapWith], reordered[index]];
    setCurrencies(reordered);

    try {
      await currencyService.reorder(reordered.map((c) => c._id));
      dispatch(fetchCurrencies());
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to reorder currencies", { variant: "error" });
      await loadCurrencies();
    }
  };

  const handleDeleteCurrency = async (currency) => {
    if (!window.confirm(`Remove ${currency.code} from the list of selectable currencies?`)) return;
    setDeletingCurrencyId(currency._id);
    try {
      await currencyService.remove(currency._id);
      enqueueSnackbar(`${currency.code} removed`, { variant: "success" });
      await loadCurrencies();
      dispatch(fetchCurrencies());
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to remove currency", { variant: "error" });
    } finally {
      setDeletingCurrencyId(null);
    }
  };
  const [savingFeatureFlag, setSavingFeatureFlag] = useState(null); // which flag key is mid-save, or null

  useEffect(() => {
    systemSettingsService
      .get()
      .then(({ data }) => {
        const s = data.data.settings;
        setSystem({
          emailSendingEnabled: s.emailSendingEnabled !== false,
          featureFlags: {
            promosEnabled: !!s.featureFlags?.promosEnabled,
            referralsEnabled: !!s.featureFlags?.referralsEnabled,
          },
          companyName: s.companyName || "",
          contactEmail: s.contactEmail || "",
          contactPhone: s.contactPhone || "",
          officeAddress: s.officeAddress || "",
          defaultDueDays: s.defaultDueDays ?? "",
          maxFileSizeMb: s.maxFileSizeMb ?? "",
          defaultCarrier: s.defaultCarrier || "",
          customsExporter: {
            name: s.customsExporter?.name || "",
            tin: s.customsExporter?.tin || "",
            address: s.customsExporter?.address || "",
            city: s.customsExporter?.city || "",
            state: s.customsExporter?.state || "",
            postalCode: s.customsExporter?.postalCode || "",
            country: s.customsExporter?.country || "USA",
            phone: s.customsExporter?.phone || "",
          },
          customsRegime: s.customsRegime || "",
          customsShippingAgent: s.customsShippingAgent || "",
          customsTermsOfDelivery: s.customsTermsOfDelivery || "",
          customsDefaultQtyUnit: s.customsDefaultQtyUnit || "",
          customsDefaultProcedureCode: s.customsDefaultProcedureCode || "",
          customsDefaultCategoryOfGoods: s.customsDefaultCategoryOfGoods ?? "",
          manifestPreparedBy: s.manifestPreparedBy || "",
          manifestAuthorizedSignature: s.manifestAuthorizedSignature || "",
          sailingCutoffs: (s.sailingCutoffs || []).map((c) => ({
            sailingDay: c.sailingDay || "",
            cutoffDay: c.cutoffDay || "",
            cutoffTime: c.cutoffTime || "12:00 PM",
            timezone: c.timezone || "America/New_York",
          })),
        });
      })
      .catch(() =>
        enqueueSnackbar("Failed to load system settings", { variant: "error" }),
      )
      .finally(() => setSystemLoading(false));
  }, []);

  const handleProfileSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/users/me`, profile);
      dispatch(setCredentials({ user: data.data.user }));
      enqueueSnackbar("Profile updated", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSystemSave = async () => {
    setSavingSystem(true);
    try {
      const { data } = await systemSettingsService.update({
        ...system,
        defaultDueDays: system.defaultDueDays ? Number(system.defaultDueDays) : undefined,
        maxFileSizeMb: system.maxFileSizeMb ? Number(system.maxFileSizeMb) : undefined,
        customsDefaultCategoryOfGoods: system.customsDefaultCategoryOfGoods
          ? Number(system.customsDefaultCategoryOfGoods)
          : undefined,
      });
      enqueueSnackbar("System settings saved", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to save settings", {
        variant: "error",
      });
    } finally {
      setSavingSystem(false);
    }
  };

  // Saves immediately on toggle, independent of the "Save Settings" button
  // below — this is a kill switch, so it should take effect the instant an
  // admin flips it, not wait for a batch save of unrelated fields.
  const handleToggleEmailSending = async (checked) => {
    setSystem((p) => ({ ...p, emailSendingEnabled: checked }));
    setSavingEmailToggle(true);
    try {
      await systemSettingsService.update({ emailSendingEnabled: checked });
      enqueueSnackbar(checked ? "Email sending re-enabled" : "Email sending disabled — no emails will go out", {
        variant: checked ? "success" : "warning",
      });
    } catch (err) {
      setSystem((p) => ({ ...p, emailSendingEnabled: !checked }));
      enqueueSnackbar(err.response?.data?.message || "Failed to update email sending toggle", {
        variant: "error",
      });
    } finally {
      setSavingEmailToggle(false);
    }
  };

  // Same instant-save pattern as email sending — a Coming Soon gate should
  // flip the moment an admin toggles it, not wait for the batch "Save
  // Settings" button. Only affects the CUSTOMER portal (see AppRouter's
  // FeatureGate); admins can always reach Promos/Referrals to keep
  // building/testing regardless of this setting.
  const handleToggleFeatureFlag = async (flag, checked) => {
    const previous = system.featureFlags;
    const next = { ...system.featureFlags, [flag]: checked };
    setSystem((p) => ({ ...p, featureFlags: next }));
    setSavingFeatureFlag(flag);
    try {
      await systemSettingsService.update({ featureFlags: next });
      enqueueSnackbar(
        checked ? "Now visible to customers" : "Hidden behind a Coming Soon page for customers",
        { variant: checked ? "success" : "warning" },
      );
    } catch (err) {
      setSystem((p) => ({ ...p, featureFlags: previous }));
      enqueueSnackbar(err.response?.data?.message || "Failed to update", { variant: "error" });
    } finally {
      setSavingFeatureFlag(null);
    }
  };

  const handleNotificationSave = async () => {
    setSavingNotifications(true);
    try {
      const { data } = await api.patch(`/users/me`, {
        notificationPreferences,
      });
      dispatch(setCredentials({ user: data.data.user }));
      enqueueSnackbar("Notification preferences saved", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to save preferences",
        {
          variant: "error",
        },
      );
    } finally {
      setSavingNotifications(false);
    }
  };

  const handlePasswordChange = async () => {
    setPwError("");
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPwError("Passwords do not match");
      return;
    }
    setSavingPw(true);
    try {
      await api.patch("/auth/change-password", {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      enqueueSnackbar("Password changed", { variant: "success" });
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setPwError(err.response?.data?.message || "Failed to change password");
    } finally {
      setSavingPw(false);
    }
  };

  const setP = (f) => (e) => setProfile((p) => ({ ...p, [f]: e.target.value }));
  const setPw = (f) => (e) =>
    setPasswords((p) => ({ ...p, [f]: e.target.value }));
  const setSys = (f) => (e) =>
    setSystem((p) => ({ ...p, [f]: e.target.value }));
  const setExporter = (f) => (e) =>
    setSystem((p) => ({
      ...p,
      customsExporter: { ...p.customsExporter, [f]: e.target.value },
    }));

  const setCutoffField = (idx, field) => (e) =>
    setSystem((p) => ({
      ...p,
      sailingCutoffs: p.sailingCutoffs.map((c, i) => (i === idx ? { ...c, [field]: e.target.value } : c)),
    }));
  const addCutoff = () =>
    setSystem((p) => ({
      ...p,
      sailingCutoffs: [
        ...p.sailingCutoffs,
        { sailingDay: "Monday", cutoffDay: "Friday", cutoffTime: "12:00 PM", timezone: "America/New_York" },
      ],
    }));
  const removeCutoff = (idx) =>
    setSystem((p) => ({ ...p, sailingCutoffs: p.sailingCutoffs.filter((_, i) => i !== idx) }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Settings"
        subtitle="Manage system and account configuration"
      />

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          <Tab
            label="My Account"
            icon={<Person sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          <Tab
            label="System"
            icon={<Settings sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          <Tab
            label="Notifications"
            icon={<Notifications sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          <Tab
            label="Security"
            icon={<Security sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          <Tab
            label="Currencies"
            icon={<AttachMoney sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
        </Tabs>

        <CardContent sx={{ maxWidth: 700 }}>
          {/* My Account */}
          {tab === 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                }}
              >
                <Avatar
                  src={user?.profilePhotoUrl}
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: "primary.main",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography fontWeight={700}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: "#001A3C",
                        color: "white",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: 10,
                      }}
                    >
                      ADMIN
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  label="First Name"
                  value={profile.firstName}
                  onChange={setP("firstName")}
                />
                <TextField
                  label="Last Name"
                  value={profile.lastName}
                  onChange={setP("lastName")}
                />
              </Box>
              <TextField
                fullWidth
                disabled
                label="Email (cannot change)"
                value={user?.email || ""}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Phone"
                value={profile.phone}
                onChange={setP("phone")}
                sx={{ mb: 2.5 }}
              />

              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleProfileSave}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Box>
          )}

          {/* System */}
          {tab === 1 && (
            systemLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: system.emailSendingEnabled ? "action.hover" : isDark ? "#4A1C1C" : "#FFF5F5",
                    border: "1px solid",
                    borderColor: system.emailSendingEnabled ? "divider" : isDark ? "#FC8181" : "#FEB2B2",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Email Sending
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {system.emailSendingEnabled
                        ? "All automated emails (customer and admin) are going out normally."
                        : "Disabled — no email is being sent to anyone, for any reason, until this is turned back on."}
                    </Typography>
                  </Box>
                  <Switch
                    checked={!!system.emailSendingEnabled}
                    onChange={(e) => handleToggleEmailSending(e.target.checked)}
                    disabled={savingEmailToggle}
                    color={system.emailSendingEnabled ? "primary" : "error"}
                  />
                </Box>

                <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                  Coming Soon Pages
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Turn ON to show customers a "Coming Soon" page instead of the real feature — for something still
                  being built/tested. Turn OFF to give customers real access. Admins can always reach Promos/Referrals
                  themselves either way.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                  {[
                    { key: "promosEnabled", label: "Promos", desc: "Promo code redemption on the customer portal." },
                    { key: "referralsEnabled", label: "Referrals", desc: "Referral program on the customer portal." },
                  ].map((f) => {
                    const comingSoon = !system.featureFlags[f.key];
                    return (
                      <Box
                        key={f.key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 2,
                          borderRadius: 2,
                          bgcolor: comingSoon ? (isDark ? "#4A2E12" : "#FFF8E1") : "action.hover",
                          border: "1px solid",
                          borderColor: comingSoon ? (isDark ? "#F6AD55" : "#FFE082") : "divider",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {f.label} — Coming Soon
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {f.desc} {comingSoon ? "Customers currently see the Coming Soon page." : "Live for customers now."}
                          </Typography>
                        </Box>
                        <Switch
                          checked={comingSoon}
                          onChange={(e) => handleToggleFeatureFlag(f.key, !e.target.checked)}
                          disabled={savingFeatureFlag === f.key}
                          color="warning"
                        />
                      </Box>
                    );
                  })}
                </Box>

                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  Company Details
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}
                >
                  <TextField
                    label="Company Name"
                    value={system.companyName}
                    onChange={setSys("companyName")}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Contact Email"
                      value={system.contactEmail}
                      onChange={setSys("contactEmail")}
                    />
                    <TextField
                      label="Contact Phone"
                      value={system.contactPhone}
                      onChange={setSys("contactPhone")}
                    />
                  </Box>
                  <TextField
                    label="Office Address"
                    value={system.officeAddress}
                    onChange={setSys("officeAddress")}
                  />
                </Box>

                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  Operational Defaults
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <TextField
                    label="Invoice Due (days)"
                    type="number"
                    value={system.defaultDueDays}
                    onChange={setSys("defaultDueDays")}
                  />
                  <TextField
                    label="Max File Size (MB)"
                    type="number"
                    value={system.maxFileSizeMb}
                    onChange={setSys("maxFileSizeMb")}
                  />
                  <TextField
                    label="Default Carrier"
                    value={system.defaultCarrier}
                    onChange={setSys("defaultCarrier")}
                  />
                </Box>

                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Customs — Exporter of Record
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Used as the Exporter on every SAD XML customs declaration generated from the Customs Queue,
                  and as the Shipper/Exporter (with TIN) on every House Bill of Lading.
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Exporter Name"
                      value={system.customsExporter.name}
                      onChange={setExporter("name")}
                    />
                    <TextField
                      label="TIN"
                      value={system.customsExporter.tin}
                      onChange={setExporter("tin")}
                    />
                  </Box>
                  <TextField
                    label="Street Address"
                    value={system.customsExporter.address}
                    onChange={setExporter("address")}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="City"
                      value={system.customsExporter.city}
                      onChange={setExporter("city")}
                    />
                    <TextField
                      label="State"
                      value={system.customsExporter.state}
                      onChange={setExporter("state")}
                      helperText="2-3 letter code"
                    />
                    <TextField
                      label="Postal Code"
                      value={system.customsExporter.postalCode}
                      onChange={setExporter("postalCode")}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Country"
                      value={system.customsExporter.country}
                      onChange={setExporter("country")}
                      helperText="3-letter code, e.g. USA"
                    />
                    <TextField
                      label="Phone"
                      value={system.customsExporter.phone}
                      onChange={setExporter("phone")}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  Customs — Declaration Defaults
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <TextField
                    label="Regime"
                    value={system.customsRegime}
                    onChange={setSys("customsRegime")}
                    helperText='e.g. "IM1"'
                  />
                  <TextField
                    label="Shipping Agent Code"
                    value={system.customsShippingAgent}
                    onChange={setSys("customsShippingAgent")}
                    helperText="2-character code"
                  />
                  <TextField
                    label="Terms of Delivery"
                    value={system.customsTermsOfDelivery}
                    onChange={setSys("customsTermsOfDelivery")}
                    helperText='e.g. "CIF"'
                  />
                  <TextField
                    label="Default Quantity Unit"
                    value={system.customsDefaultQtyUnit}
                    onChange={setSys("customsDefaultQtyUnit")}
                    helperText='e.g. "NO"'
                  />
                  <TextField
                    label="Default Procedure Code"
                    value={system.customsDefaultProcedureCode}
                    onChange={setSys("customsDefaultProcedureCode")}
                    helperText='e.g. "HOME"'
                  />
                  <TextField
                    label="Default Category of Goods"
                    type="number"
                    value={system.customsDefaultCategoryOfGoods}
                    onChange={setSys("customsDefaultCategoryOfGoods")}
                  />
                </Box>

                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Manifest — Signature Block
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Printed at the bottom of every exported Manifest .xlsx.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <TextField
                    label="Prepared By"
                    value={system.manifestPreparedBy}
                    onChange={setSys("manifestPreparedBy")}
                    helperText="Name printed on the Prepared By line"
                  />
                  <TextField
                    label="Authorized Signature"
                    value={system.manifestAuthorizedSignature}
                    onChange={setSys("manifestAuthorizedSignature")}
                    helperText="Name/title printed on the signature line"
                  />
                </Box>

                <Divider sx={{ my: 2.5 }} />
                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                  Sailing Cutoffs
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  When a consolidation request must be in by, per sailing day — drives the
                  countdown on the dashboard and the day-before reminder to admins.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1.5 }}>
                  {system.sailingCutoffs.map((c, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr auto" },
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        select
                        size="small"
                        label="Sailing Day"
                        value={c.sailingDay}
                        onChange={setCutoffField(idx, "sailingDay")}
                      >
                        {WEEKDAY_OPTIONS.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Cutoff Day"
                        value={c.cutoffDay}
                        onChange={setCutoffField(idx, "cutoffDay")}
                      >
                        {WEEKDAY_OPTIONS.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label="Cutoff Time"
                        value={c.cutoffTime}
                        onChange={setCutoffField(idx, "cutoffTime")}
                        placeholder="12:00 PM"
                        helperText="e.g. 12:00 PM (Eastern Time)"
                      />
                      <IconButton size="small" color="error" onClick={() => removeCutoff(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {system.sailingCutoffs.length === 0 && (
                    <Typography variant="caption" color="text.disabled">
                      No cutoff rules configured — the default Thursday/Monday sailing schedule
                      is used until you add one.
                    </Typography>
                  )}
                </Box>
                <Button size="small" startIcon={<Add />} onClick={addCutoff} sx={{ mb: 2.5 }}>
                  Add Cutoff Rule
                </Button>

                <Box>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSystemSave}
                    disabled={savingSystem}
                  >
                    {savingSystem ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      "Save Settings"
                    )}
                  </Button>
                </Box>
              </Box>
            )
          )}

          {/* Notifications */}
          {tab === 2 && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                display="block"
                mb={2}
              >
                Email Notifications
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[
                  {
                    key: "newShipment",
                    label: "New shipment created",
                    sub: "When a customer creates a new shipment",
                  },
                  {
                    key: "statusChange",
                    label: "Shipment status change",
                    sub: "When a shipment status is updated",
                  },
                  {
                    key: "payment",
                    label: "Payment received",
                    sub: "When a customer makes a payment",
                  },
                  {
                    key: "overdueAlert",
                    label: "Overdue invoice alerts",
                    sub: "Daily digest of overdue invoices",
                  },
                  {
                    key: "dailyDigest",
                    label: "Daily summary digest",
                    sub: "Daily overview of operations at 8am",
                  },
                ].map((n) => (
                  <Box
                    key={`email-${n.key}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {n.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {n.sub}
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationPreferences.email[n.key]}
                      onChange={(e) =>
                        setNotificationPreferences((p) => ({
                          ...p,
                          email: {
                            ...p.email,
                            [n.key]: e.target.checked,
                          },
                        }))
                      }
                      size="small"
                    />
                  </Box>
                ))}
              </Box>

              <Typography
                variant="overline"
                color="text.secondary"
                display="block"
                mb={2}
                mt={4}
              >
                In-App Notifications
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[
                  {
                    key: "newShipment",
                    label: "New shipment created",
                    sub: "When a customer creates a new shipment",
                  },
                  {
                    key: "statusChange",
                    label: "Shipment status change",
                    sub: "When a shipment status is updated",
                  },
                  {
                    key: "payment",
                    label: "Payment received",
                    sub: "When a customer makes a payment",
                  },
                  {
                    key: "overdueAlert",
                    label: "Overdue invoice alerts",
                    sub: "Daily digest of overdue invoices",
                  },
                  {
                    key: "dailyDigest",
                    label: "Daily summary digest",
                    sub: "Daily overview of operations at 8am",
                  },
                ].map((n) => (
                  <Box
                    key={`inapp-${n.key}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {n.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {n.sub}
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationPreferences.inApp[n.key]}
                      onChange={(e) =>
                        setNotificationPreferences((p) => ({
                          ...p,
                          inApp: {
                            ...p.inApp,
                            [n.key]: e.target.checked,
                          },
                        }))
                      }
                      size="small"
                    />
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                sx={{ mt: 2.5 }}
                startIcon={<Save />}
                onClick={handleNotificationSave}
                disabled={savingNotifications}
              >
                {savingNotifications ? "Saving..." : "Save Preferences"}
              </Button>
            </Box>
          )}

          {/* Security */}
          {tab === 3 && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                display="block"
                mb={2}
              >
                Change Password
              </Typography>
              {pwError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2 }}
                  onClose={() => setPwError("")}
                >
                  {pwError}
                </Alert>
              )}
              {/* A real <form> boundary, not just a styled Box — without one,
                  Chrome has no form element to scope its autofill grouping
                  to, so when an admin picks a saved credential from the
                  password manager dropdown it searches the WHOLE page for
                  something to treat as "username" and finds the sidebar's
                  nav search input instead (autoComplete="off" alone doesn't
                  stop this — Chrome deliberately ignores it for login-
                  relevant heuristics). The hidden username input right
                  after <form> gives it a legitimate, in-bounds target so it
                  never needs to look outside this form at all. */}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePasswordChange();
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={user?.email || ""}
                  readOnly
                  style={{ display: "none" }}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <TextField
                  label="Current Password"
                  type={showPw ? "text" : "password"}
                  value={passwords.currentPassword}
                  onChange={setPw("currentPassword")}
                  name="currentPassword"
                  // "off" rather than the spec-correct "current-password" —
                  // that value is exactly what tells Chrome's password
                  // manager "autofill the saved login password here," which
                  // is the unwanted auto-fill the admin reported. This isn't
                  // a login form, so there's no saved value that SHOULD be
                  // suggested.
                  autoComplete="off"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() => setShowPw((v) => !v)}
                            sx={{ minWidth: 0, p: 0.5 }}
                          >
                            {showPw ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </Button>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label="New Password"
                  type={showPw ? "text" : "password"}
                  value={passwords.newPassword}
                  onChange={setPw("newPassword")}
                  name="newPassword"
                  autoComplete="new-password"
                  helperText="Min. 8 characters, one uppercase, one number"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() => setShowPw((v) => !v)}
                            sx={{ minWidth: 0, p: 0.5 }}
                          >
                            {showPw ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </Button>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label="Confirm New Password"
                  type={showPw ? "text" : "password"}
                  value={passwords.confirmPassword}
                  onChange={setPw("confirmPassword")}
                  name="confirmNewPassword"
                  autoComplete="new-password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() => setShowPw((v) => !v)}
                            sx={{ minWidth: 0, p: 0.5 }}
                          >
                            {showPw ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </Button>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ alignSelf: "flex-start" }}
                  disabled={
                    savingPw ||
                    !passwords.currentPassword ||
                    !passwords.newPassword
                  }
                >
                  {savingPw ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />
              <Typography
                variant="overline"
                color="text.secondary"
                display="block"
                mb={2}
              >
                Active Sessions
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Current Session
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {navigator.userAgent.includes("Chrome")
                      ? "Chrome"
                      : "Browser"}{" "}
                    · {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "success.light",
                    color: "success.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  Active
                </Typography>
              </Box>
            </Box>
          )}

          {/* Currencies */}
          {tab === 4 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                Currencies
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Controls which currencies show up in the "Total Invoice Amount" / item cost
                dropdowns on shipment forms (web and mobile). Exchange rates are always fetched
                live when a shipment is created — adding a currency here doesn't require a rate
                to be configured anywhere.
              </Typography>

              {currenciesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
                  {currencies.map((currency, index) => (
                    <Box
                      key={currency._id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          disabled={index === 0}
                          onClick={() => handleMoveCurrency(index, -1)}
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          disabled={index === currencies.length - 1}
                          onClick={() => handleMoveCurrency(index, 1)}
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </Box>
                      <CurrencyFlag code={currency.code} size={20} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography fontWeight={700}>
                          {currency.code} — {getCurrencyCountry(currency.code)?.countryName || "—"}
                          {currency.isSystem && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, color: "primary.main", fontWeight: 600 }}
                            >
                              System
                            </Typography>
                          )}
                        </Typography>
                        {currency.name && (
                          <Typography variant="caption" color="text.secondary">
                            {currency.name}
                          </Typography>
                        )}
                      </Box>
                      <Switch
                        size="small"
                        checked={currency.isActive}
                        disabled={currency.isSystem || togglingCurrencyId === currency._id}
                        onChange={() => handleToggleCurrencyActive(currency)}
                      />
                      <IconButton
                        size="small"
                        disabled={currency.isSystem || deletingCurrencyId === currency._id}
                        onClick={() => handleDeleteCurrency(currency)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 2.5 }} />

              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Add a Currency
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                <Autocomplete
                  size="small"
                  options={currencyPickerOptions}
                  value={currencyToAdd}
                  onChange={(_, val) => setCurrencyToAdd(val)}
                  getOptionLabel={(opt) => `${opt.code} — ${opt.name}`}
                  isOptionEqualToValue={(opt, val) => opt.code === val.code}
                  sx={{ flexGrow: 1, minWidth: 280 }}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <CurrencyFlag code={opt.code} />
                      <span>
                        {opt.code} — {opt.name}
                      </span>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Search currencies to add" placeholder="e.g. KYD or Dollar" />
                  )}
                />
                <Button
                  variant="contained"
                  startIcon={savingCurrency ? <CircularProgress size={16} color="inherit" /> : <Add />}
                  onClick={handleAddCurrency}
                  disabled={savingCurrency || !currencyToAdd}
                >
                  Add
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminSettings;
